import { Component, ReactElement } from 'react';
import { HelmetWithTitleAndDescription } from '../../../../../../shared/frontend/components/helmetHelper';
import { DisableAdsOnPage } from '../../../../../../shared/frontend/components/ads/disableAdsOnPage';
import { SECURITY_DISCLOSURE_CONTACT_EMAIL } from '../../../../../../shared/common/securityDisclosure';
import styles from './policyPageStyles';

/* tslint:disable:max-line-length */

export default class SecurityDisclosurePage extends Component<any, {}> {
  public render(): ReactElement<any> {
    return (
      <div>
        <HelmetWithTitleAndDescription title="Security / Vulnerability Disclosure Policy" />
        <DisableAdsOnPage />
        <div>
          <div className="slds-p-around--large text-white" style={{ backgroundColor: '#4f4b49' }}>
            <div className={styles.legals}>
              <h1 className="slds-text-heading--large slds-m-bottom--large">
                Security / Vulnerability Disclosure Policy
              </h1>
              <p>Last updated: March 30, 2026</p>

              <p>
                Raider.IO welcomes good-faith security research and responsible vulnerability disclosure. This page
                explains how to report security issues to us and what we ask researchers to do while validating a
                potential issue.
              </p>

              <p>
                This is a vulnerability disclosure path, not a paid bug bounty program. We do not offer guaranteed
                compensation, rewards, or bounty payments for submitted reports, and submission of a report does not
                create any expectation of compensation.
              </p>

              <p>
                Raider.IO does not engage with reports that intentionally withhold vulnerability details or request
                payment prior to full disclosure.
              </p>

              <h2>How to Report a Security Issue</h2>

              <p>
                Please report suspected vulnerabilities by emailing{' '}
                <a href={`mailto:${SECURITY_DISCLOSURE_CONTACT_EMAIL}`}>{SECURITY_DISCLOSURE_CONTACT_EMAIL}</a>.
              </p>

              <p>Please include as much of the following as you can:</p>
              <ul className={styles.ul}>
                <li>The affected URL, feature, workflow, or service.</li>
                <li>Clear reproduction steps.</li>
                <li>The security impact you believe the issue has.</li>
                <li>Screenshots, logs, or a proof of concept when it is safe to share them.</li>
                <li>Your contact information if you want follow-up.</li>
              </ul>

              <h2>What You Can Expect From Us</h2>

              <ul className={styles.ul}>
                <li>We aim to acknowledge receipt within 5 business days.</li>
                <li>We will share status updates when practical.</li>
                <li>We may ask follow-up questions if we need more detail to validate or remediate the issue.</li>
              </ul>

              <p>
                Response times and resolution are not guaranteed and may vary depending on the nature and complexity of
                the report.
              </p>

              <h2>Good-Faith Testing Expectations</h2>

              <p>While researching a vulnerability, please:</p>
              <ul className={styles.ul}>
                <li>Test only what is necessary to confirm the issue.</li>
                <li>
                  Do not access, alter, or retain more user data than is reasonably necessary to demonstrate the issue.
                </li>
                <li>
                  Do not use social engineering, phishing, physical intrusion, spam, denial of service, or destructive
                  testing.
                </li>
                <li>Do not establish persistence, plant payloads, or exfiltrate data.</li>
                <li>
                  Stop and report immediately if you encounter sensitive data that you were not intending to access.
                </li>
              </ul>

              <h2>Coordinated Disclosure</h2>

              <p>
                Please give Raider.IO a reasonable opportunity to investigate and remediate a reported issue before
                public disclosure. Do not publicly disclose the vulnerability before coordinating with us unless we
                explicitly agree otherwise.
              </p>

              <h2>Scope</h2>

              <p>The following Raider.IO-controlled targets are generally in scope for this policy:</p>
              <ul className={styles.ul}>
                <li>raider.io</li>
                <li>classic.raider.io</li>
                <li>era.raider.io</li>
                <li>raiderioapp.com</li>
                <li>keystone.guru</li>
                <li>Other web properties and services that Raider.IO clearly presents as Raider.IO-controlled</li>
              </ul>

              <p>The following are generally out of scope for this policy:</p>
              <ul className={styles.ul}>
                <li>Third-party vendors, platforms, or services that Raider.IO uses</li>
                <li>
                  Issues that belong to a vendor unless the vulnerability is caused by Raider.IO&apos;s integration or
                  implementation
                </li>
              </ul>

              <p>If you are unsure whether a target is in scope, ask before testing it.</p>

              <h2>Safe Harbor</h2>

              <p>
                If you make a good-faith effort to follow this policy, we will treat your research conducted in
                accordance with under this policy as authorized. This statement is intended to support coordinated
                security research, does not apply to activity outside this policy, and is not legal advice.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
